/**
 * Inputs: next trip document + blob etag when the GET JSON included one.
 * Outputs: save result (ok / conflict / error). Offline pack is a family read fallback, not an editing session.
 * Production Netlify strips If-Match; the server also accepts document.revision.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { ThaiPhrase, TripDocument } from "@shared/schema.ts";
import { mergeOwnerDraftDetailed } from "@shared/merge-trip.ts";
import { applyPhraseWrite, type PhraseWrite } from "@shared/phrase-write.ts";
import { injectNow } from "@shared/time.ts";
import { shouldApplyRemoteTrip } from "@shared/trip-sync.ts";
import { fetchTrip, saveTrip, saveTripPhraseWrite } from "../lib/api.ts";
import { readCachedTrip, verifyPackReady } from "../lib/offline.ts";
import { readDevicePreviewNow, writeDevicePreviewNow } from "../lib/household-view.ts";
import { useSession } from "./session.tsx";

type Conflict = { trip: TripDocument; etag: string };
export type SaveResult =
  | { ok: true; trip: TripDocument }
  | { ok: false; kind: "conflict" | "error" | "offline" | "readonly"; message: string };

type TripState = {
  loading: boolean;
  refreshing: boolean;
  empty: boolean;
  trip: TripDocument | null;
  etag: string | null;
  error: string | null;
  offline: boolean;
  syncState: "up_to_date" | "offline" | "server_unavailable" | "session_expired" | "stale";
  lastUpdatedAt: string | null;
  draft: TripDocument | null;
  conflict: Conflict | null;
  changeNotice: { summary: string; href?: string } | null;
  saveState: "idle" | "saving" | "saved" | "error";
  devicePreviewNow: string | null;
  setDevicePreviewNow: (value: string | null) => void;
  refresh: () => Promise<void>;
  save: (next: TripDocument, etagOverride?: string) => Promise<SaveResult>;
  savePhrases: (next: ThaiPhrase[]) => Promise<SaveResult>;
  savePhraseWrite: (write: PhraseWrite) => Promise<SaveResult>;
  reloadRemote: () => void;
  reapplyDraft: () => Promise<SaveResult>;
  clearError: () => void;
};

const TripContext = createContext<TripState | null>(null);

export function TripProvider({ children }: { children: ReactNode }) {
  const { authenticated, offlineReader, markSessionExpired } = useSession();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [trip, setTrip] = useState<TripDocument | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [syncState, setSyncState] = useState<TripState["syncState"]>(navigator.onLine ? "up_to_date" : "offline");
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [draft, setDraft] = useState<TripDocument | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [changeNotice, setChangeNotice] = useState<TripState["changeNotice"]>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [devicePreviewNow, setDevicePreviewNowState] = useState<string | null>(readDevicePreviewNow);
  const etagRef = useRef<string | null>(null);
  const draftRef = useRef<TripDocument | null>(null);
  const tripRef = useRef<TripDocument | null>(null);
  const conflictRef = useRef<Conflict | null>(null);
  const mutationSeqRef = useRef(0);
  const appliedRevisionRef = useRef<number | null>(null);
  const saveInFlightRef = useRef(false);
  etagRef.current = etag;
  draftRef.current = draft;
  tripRef.current = trip;
  conflictRef.current = conflict;

  function setDevicePreviewNow(value: string | null) {
    writeDevicePreviewNow(value);
    setDevicePreviewNowState(value);
  }

  const loadFromPack = useCallback(async (message: string) => {
    const cached = await readCachedTrip();
    const verified = await verifyPackReady();
    if (cached?.trip && verified.ok) {
      setTrip(cached.trip as TripDocument);
      setEmpty(false);
      setOffline(true);
      setSyncState("offline");
      setError(message);
      setLastUpdatedAt(verified.generatedAt ?? null);
      return true;
    }
    return false;
  }, []);

  const refresh = useCallback(async () => {
    if (!authenticated) {
      setTrip(null);
      setLoading(false);
      return;
    }
    const background = Boolean(tripRef.current);
    if (offlineReader) {
      if (!background) setLoading(true);
      else setRefreshing(true);
      const ok = await loadFromPack("Showing the downloaded pack. This device is in read-only offline mode.");
      if (!ok) setError("The downloaded pack is missing or incomplete.");
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (!background) setLoading(true);
    else setRefreshing(true);
    const mutationSeqAtStart = mutationSeqRef.current;
    const isInitialLoad = !tripRef.current;
    try {
      const result = await fetchTrip();
      if (!shouldApplyRemoteTrip({
        incomingRevision: result.trip?.revision ?? -1,
        appliedRevision: appliedRevisionRef.current,
        mutationSeqAtStart,
        mutationSeqNow: mutationSeqRef.current,
        conflictOpen: Boolean(conflictRef.current),
        isInitialLoad,
        saveInFlight: saveInFlightRef.current,
      })) {
        return;
      }
      const previous = tripRef.current;
      setEmpty(result.empty);
      setTrip(result.trip);
      setEtag(result.etag);
      etagRef.current = result.etag;
      if (result.trip) appliedRevisionRef.current = result.trip.revision;
      setError(null);
      setOffline(false);
      setSyncState("up_to_date");
      if (!background) setConflict(null);
      if (!draftRef.current) setDraft(null);
      setLastUpdatedAt(new Date().toISOString());
      if (background && previous && result.trip && result.trip.revision !== previous.revision) {
        const latest = result.trip.revisions[result.trip.revisions.length - 1];
        setChangeNotice({
          summary: latest?.summary ?? "The shared trip was updated.",
          href: "/today",
        });
      }
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        markSessionExpired();
        setSyncState("session_expired");
        setError("Sign in again to keep editing. The downloaded pack can still be opened.");
        await loadFromPack("Session ended. Showing the downloaded pack.");
      } else {
        const packed = await loadFromPack("Showing the downloaded pack. Reconnect to refresh.");
        if (!packed) {
          setSyncState("server_unavailable");
          setError(err instanceof Error ? err.message : "Could not load the trip.");
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authenticated, offlineReader, loadFromPack, markSessionExpired]);

  const save = useCallback(async (next: TripDocument, etagOverride?: string): Promise<SaveResult> => {
    if (offlineReader) {
      setDraft(next);
      setSaveState("error");
      return { ok: false, kind: "readonly", message: "This device is in offline read mode. Reconnect and sign in to save." };
    }
    const match = etagOverride ?? etagRef.current ?? "";
    if (!navigator.onLine) {
      setDraft(next);
      setSaveState("error");
      setSyncState("offline");
      return { ok: false, kind: "offline", message: "Offline. The draft is kept on this phone until you reconnect." };
    }
    setSaveState("saving");
    setDraft(next);
    saveInFlightRef.current = true;
    try {
      const result = await saveTrip(next, match);
      mutationSeqRef.current += 1;
      appliedRevisionRef.current = result.trip.revision;
      setTrip(result.trip);
      setEtag(result.etag);
      etagRef.current = result.etag;
      setDraft(null);
      setConflict(null);
      setError(null);
      setSaveState("saved");
      setSyncState("up_to_date");
      setLastUpdatedAt(new Date().toISOString());
      return { ok: true, trip: result.trip };
    } catch (err) {
      const failed = err as { status?: number; data?: { trip?: TripDocument; etag?: string }; malformed?: boolean };
      if (failed.malformed) {
        setSaveState("error");
        const message = err instanceof Error ? err.message : "Save reply was incomplete.";
        setError(message);
        return { ok: false, kind: "error", message };
      }
      if (failed.status === 409 && failed.data?.trip && failed.data.etag) {
        setDraft(next);
        setConflict({ trip: failed.data.trip, etag: failed.data.etag });
        setError("This trip changed on another device. Reload, or reapply your draft.");
        setSaveState("error");
        return { ok: false, kind: "conflict", message: "This trip changed on another device." };
      }
      if (failed.status === 401) {
        markSessionExpired();
        setSyncState("session_expired");
      }
      setSaveState("error");
      const message = err instanceof Error ? err.message : "Could not save.";
      setError(message);
      return { ok: false, kind: "error", message };
    } finally {
      saveInFlightRef.current = false;
    }
  }, [offlineReader, markSessionExpired]);

  const savePhraseWrite = useCallback(async (write: PhraseWrite): Promise<SaveResult> => {
    const current = tripRef.current;
    if (!current) {
      setSaveState("error");
      return { ok: false, kind: "error", message: "The trip is not loaded yet." };
    }
    const drafted = { ...current, thaiPhrases: applyPhraseWrite(current.thaiPhrases, write) };
    if (offlineReader) {
      setDraft(drafted);
      setSaveState("error");
      return { ok: false, kind: "readonly", message: "This device is in offline read mode. Reconnect and sign in to save." };
    }
    const match = etagRef.current ?? "";
    if (!navigator.onLine) {
      setDraft(drafted);
      setSaveState("error");
      setSyncState("offline");
      return { ok: false, kind: "offline", message: "Offline. The draft is kept on this phone until you reconnect." };
    }
    setSaveState("saving");
    setDraft(drafted);
    saveInFlightRef.current = true;
    try {
      let saved: { trip: TripDocument; etag: string | null };
      try {
        saved = await saveTripPhraseWrite(write, match, current.revision);
      } catch (err) {
        const status = (err as { status?: number }).status;
        if (status === 404) {
          saved = await saveTrip(drafted, match);
        } else {
          throw err;
        }
      }
      mutationSeqRef.current += 1;
      appliedRevisionRef.current = saved.trip.revision;
      setTrip(saved.trip);
      setEtag(saved.etag);
      etagRef.current = saved.etag;
      setDraft(null);
      setConflict(null);
      setError(null);
      setSaveState("saved");
      setSyncState("up_to_date");
      setLastUpdatedAt(new Date().toISOString());
      return { ok: true, trip: saved.trip };
    } catch (err) {
      const failed = err as { status?: number; data?: { trip?: TripDocument; etag?: string }; malformed?: boolean };
      if (failed.malformed) {
        setSaveState("error");
        const message = err instanceof Error ? err.message : "Save reply was incomplete.";
        setError(message);
        return { ok: false, kind: "error", message };
      }
      if (failed.status === 409 && failed.data?.trip && failed.data.etag) {
        setDraft(drafted);
        setConflict({ trip: failed.data.trip, etag: failed.data.etag });
        setError("This trip changed on another device. Reload, or reapply your draft.");
        setSaveState("error");
        return { ok: false, kind: "conflict", message: "This trip changed on another device. Use Reload or Reapply my draft." };
      }
      if (failed.status === 401) {
        setSyncState("session_expired");
        setSaveState("error");
        return { ok: false, kind: "error", message: "Sign in again to save this card." };
      }
      if (failed.status === 403) {
        setSaveState("error");
        return { ok: false, kind: "error", message: "Only the owner can save Thai cards." };
      }
      setSaveState("error");
      const message = err instanceof Error ? err.message : "Could not save the card.";
      setError(message);
      return { ok: false, kind: "error", message };
    } finally {
      saveInFlightRef.current = false;
    }
  }, [offlineReader]);

  const savePhrases = useCallback(async (next: ThaiPhrase[]): Promise<SaveResult> => {
    return savePhraseWrite({ action: "merge", phrases: next });
  }, [savePhraseWrite]);

  function reloadRemote() {
    if (!conflict) return;
    setTrip(conflict.trip);
    setEtag(conflict.etag);
    etagRef.current = conflict.etag;
    appliedRevisionRef.current = conflict.trip.revision;
    setDraft(null);
    setConflict(null);
    setError(null);
    setSaveState("idle");
  }

  const reapplyDraft = useCallback(async () => {
    if (!draftRef.current || !conflict) {
      return { ok: false, kind: "error", message: "Nothing to reapply." } satisfies SaveResult;
    }
    const merged = mergeOwnerDraftDetailed(conflict.trip, draftRef.current, tripRef.current);
    const result = await save(merged.doc, conflict.etag);
    if (result.ok && merged.conflicts.length) {
      setError(`Saved, but overlapping edits were kept from the newer trip (${merged.conflicts.map((row) => row.field).slice(0, 4).join(", ")}).`);
    }
    return result;
  }, [conflict, save]);

  useEffect(() => {
    void refresh();
  }, [authenticated, refresh]);

  useEffect(() => {
    function onVis() {
      if (document.visibilityState === "visible") void refresh();
    }
    function onOnline() {
      setOffline(false);
      void refresh();
    }
    function onOffline() {
      setOffline(true);
      setSyncState("offline");
    }
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible" && navigator.onLine && !offlineReader) void refresh();
    }, 60_000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.clearInterval(timer);
    };
  }, [refresh, offlineReader]);

  return (
    <TripContext.Provider
      value={{
        loading,
        refreshing,
        empty,
        trip,
        etag,
        error,
        offline,
        syncState,
        lastUpdatedAt,
        draft,
        conflict,
        changeNotice,
        saveState,
        devicePreviewNow,
        setDevicePreviewNow,
        refresh,
        save,
        savePhrases,
        savePhraseWrite,
        reloadRemote,
        reapplyDraft,
        clearError: () => setError(null),
      }}
    >
      {children}
    </TripContext.Provider>
  );
}

export function useTrip() {
  const value = useContext(TripContext);
  if (!value) throw new Error("Trip missing");
  return value;
}

export function useTripClock(): Date {
  const { trip, devicePreviewNow } = useTrip();
  const { role } = useSession();
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setTick(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  if (trip?.fixtureKind === "synthetic" && trip.trip.previewNow) return injectNow(trip.trip.previewNow);
  if (role === "owner" && devicePreviewNow) return injectNow(devicePreviewNow);
  return new Date(tick);
}
